import { candidateSeatKey, type SeatCandidate } from '@anjabom/core';

const DEVICE_SECRET_KEY = 'anjabom.device-secret.v1';

export interface DecisionSyncResult {
  status: 'synced' | 'local_only';
  reason?: 'mixed_context' | 'provider_unavailable' | 'network_error';
  decisionId?: string;
}

export function decisionDeviceSecret(): string {
  const current = window.localStorage.getItem(DEVICE_SECRET_KEY);
  if (current && /^[A-Za-z0-9_-]{43}$/.test(current)) return current;
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  const secret = btoa(String.fromCharCode(...bytes)).replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/u, '');
  window.localStorage.setItem(DEVICE_SECRET_KEY, secret);
  return secret;
}

export async function syncSeatDecision(candidates: SeatCandidate[], chosenCandidateId: string, clientOpId: string): Promise<DecisionSyncResult> {
  const chosen = candidates.find((candidate) => candidate.candidateId === chosenCandidateId);
  const first = candidates[0];
  if (!chosen || !first) return { status: 'local_only', reason: 'mixed_context' };
  const sameContext = candidates.every((candidate) => candidate.venueId === first.venueId && candidate.spaceId === first.spaceId && candidate.layoutVersionId === first.layoutVersionId && candidate.instanceId === first.instanceId);
  if (!sameContext) return { status: 'local_only', reason: 'mixed_context' };

  try {
    const response = await fetch('/api/decisions', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        venueId: first.venueId,
        layoutVersionId: first.layoutVersionId,
        instanceId: first.instanceId,
        candidates: candidates.map(({ sectionCode, rowLabel, seatNumber, expectedPrice }) => ({ sectionCode, rowLabel, seatNumber, expectedPrice })),
        chosenSeatKey: candidateSeatKey(chosen),
        deviceSecret: decisionDeviceSecret(),
        clientOpId,
      }),
      signal: AbortSignal.timeout(8_000),
    });
    if (!response.ok) return { status: 'local_only', reason: 'provider_unavailable' };
    const value = await response.json() as { decisionId?: string };
    return typeof value.decisionId === 'string' ? { status: 'synced', decisionId: value.decisionId } : { status: 'synced' };
  } catch {
    return { status: 'local_only', reason: 'network_error' };
  }
}
